/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';
import { catOptions } from 'fp-ts/lib/Array';
import { fromNullable, none, Option, some } from 'fp-ts/lib/Option';
import Collection from 'ol/Collection';
import * as condition from 'ol/events/condition';
import Feature, { FeatureLike } from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import * as interaction from 'ol/interaction';
import { SelectEvent } from 'ol/interaction/Select';
import { Layer } from 'ol/layer';
import Vector from 'ol/layer/Vector';
import Map from 'ol/Map';
import LayerRenderer from 'ol/renderer/Layer';
import Source from 'ol/source/Source';
import SourceVector from 'ol/source/Vector';
import Circle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import {
    ensureArray,
    eqFeaturePathList,
    tryFeaturePath,
    tryString,
} from '../../util';
import {
    AboutToRefreshEvent,
    deserializeFeaturePath,
    serializeFeaturePath,
    withInteraction,
} from '../functions';
import type {
    FeaturePath,
    FilterFunction,
    InteractionSelect,
    MultiSelectOptions,
    NonNullFeaturePath,
    SelectOptions,
    SingleSelectOptions,
    VectorLayer,
} from '../types';

const logger = debug('sdi:map/select');

// const fontSizeIncrement = (s: string) => {
//     const result = fontSizeExtractRegexp.exec(s);
//     if (!result) {
//         return s;
//     }
//     if (result.length !== 2) {
//         return s;
//     }
//     const ret = parseFloat(result[1]) * 1.3;
//     if (isNaN(ret)) {
//         return s;
//     }
//     return s.replace(fontSizeReplaceRegexp,
//         (_m: string, p1: string, p2: string) => (
//             `${p1} ${ret.toFixed(1)}px ${p2}`
//         ));
// };

// const getSelectionStyleForPoint = (style: olStyle.Style) => {
//     const text = style.getText();
//     if (text && text.getText()) {
//         return (new olStyle.Style({
//             text: new olStyle.Text({
//                 font: fontSizeIncrement(text.getFont()),
//                 text: text.getText(),
//                 textAlign: text.getTextAlign(),
//                 textBaseline: text.getTextBaseline(),
//                 offsetX: text.getOffsetX(),
//                 offsetY: text.getOffsetY(),
//                 fill: new olStyle.Fill({
//                     color: '#3FB2FF',
//                 }),
//                 stroke: new olStyle.Stroke({
//                     width: 2,
//                     color: 'white',
//                 }),
//             }),
//         }));
//     }
//     return (new olStyle.Style());
// };

const selectColorBase = '#87cfff';
// const selectColorBase = '#ff2c2c'; // DEBUG friendly

const getSelectionStyleForPolygon = (style: Style[] | null) => {
    const selectStyle = [
        new Style({
            stroke: new Stroke({
                width: 7,
                color: 'white',
            }),
        }),
        new Style({
            stroke: new Stroke({
                width: 2,
                color: selectColorBase,
            }),
        }),
    ];

    return fromNullable(style)
        .map(s => s.concat(selectStyle))
        .getOrElse(selectStyle);
};

const getSelectionStyleForPoint = (style: Style[] | null) => {
    const selectStyle = new Style({
        image: new Circle({
            radius: 12,
            fill: new Fill({
                color: selectColorBase,
            }),
            stroke: new Stroke({
                width: 2,
                color: 'white',
            }),
        }),
    });
    // Get the label from original style to keep it on selection
    // !fragile: we take for granted that the label-text is the last element in the style array
    // (icons are also considered as label and we don't want to keep them)
    return fromNullable(style)
        .chain(s => {
            if (s.length > 1) {
                return some(
                    s.reduce((acc, val) => {
                        acc.setText(val.getText());
                        return acc;
                    }, selectStyle),
                );
            }
            return none as Option<Style>;
        })
        .getOrElse(selectStyle);
};
const getSelectionStyleForLine = (style: Style[] | null) => {
    const selectStyle = [
        new Style({
            stroke: new Stroke({
                width: 4,
                color: 'white',
            }),
        }),
        new Style({
            stroke: new Stroke({
                width: 2,
                color: selectColorBase,
            }),
        }),
    ];
    return fromNullable(style)
        .map(s => s.concat(selectStyle))
        .getOrElse(selectStyle);
};

const getStylesForFeature = (
    layers: Collection<VectorLayer>,
    f: Feature,
    res: number,
): Style[] | null => {
    // const fn = f.getStyleFunction();
    // if (fn) {
    //     return ensureArray<Style>(fn.call(f, res));
    // }
    // const fs = f.getStyle();
    // if (fs) {
    //     if (typeof fs === 'function') {
    //         return ensureArray<Style>(fs.call(f, res));
    //     }
    //     return ensureArray(fs);
    // }

    const layerRef = fromNullable(f.get('lid')).chain(lid =>
        fromNullable(layers.getArray().find(l => l.get('id') === lid)),
    );

    return layerRef
        .map(layer => {
            const layerFn = layer.getStyleFunction();
            if (layerFn) {
                const s = layerFn(f, res);
                if (s && ensureArray(s).length > 0) {
                    return ensureArray(s);
                }
            }

            const layerFs = layer.getStyle();
            if (layerFs) {
                if (typeof layerFs === 'function') {
                    const s = layerFs(f, res);
                    if (s) {
                        const styleArray = ensureArray(s);
                        if (styleArray.length > 0) {
                            return styleArray;
                        }
                    }
                } else {
                    return ensureArray(layerFs);
                }
            }
            return null;
        })
        .getOrElse(null);
};
// const selectionStyle =
//     (layers: Collection<layer.Vector>) =>
//         (f: Feature, res: number) => {
//             const geometryType = f.getGeometry().getType();
//             if (geometryType === 'Point') {
//                 const styles = getStylesForFeature(layers, f, res);
//                 if (styles) {
//                     return styles.map(getSelectionStyleForPoint);
//                 }
//             }
//             else if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
//                 return [
//                     new olStyle.Style({
//                         stroke: new olStyle.Stroke({
//                             width: 4,
//                             color: 'white',
//                         }),
//                     }),
//                     new olStyle.Style({
//                         stroke: new olStyle.Stroke({
//                             width: 2,
//                             color: '#3FB2FF',
//                         }),
//                     }),
//                 ];
//             }

//             return [new olStyle.Style({
//                 fill: new olStyle.Fill({
//                     color: '#3FB2FF',
//                 }),
//                 stroke: new olStyle.Stroke({
//                     width: 2,
//                     color: 'white',
//                 }),
//             })];
//         };

const selectionStyle =
    (layers: Collection<VectorLayer>) =>
    (f: Feature<Geometry>, res: number) => {
        const geom = f.getGeometry();
        const styles = getStylesForFeature(layers, f, res);

        if (geom !== undefined) {
            const geometryType = geom.getType();
            if (geometryType === 'Point' || geometryType === 'MultiPoint') {
                return getSelectionStyleForPoint(styles);
            } else if (
                geometryType === 'LineString' ||
                geometryType === 'MultiLineString'
            ) {
                return getSelectionStyleForLine(styles);
            }
            return getSelectionStyleForPolygon(styles);
        }
        logger(`GEOM: ${geom}`);
        return [];
    };

const pathFromOriginalFeature = (feature: Feature<Geometry>) =>
    fromNullable(feature.get('lid'))
        .chain(tryString)
        .chain(layerId =>
            fromNullable(feature.getId()).map(featureId => ({
                layerId,
                featureId,
            })),
        );

const pathFromClonedFeature = (feature: Feature<Geometry>) =>
    fromNullable(feature.getId()).chain(deserializeFeaturePath);

const findFeature = (layers: Collection<VectorLayer>, path: FeaturePath) =>
    tryFeaturePath(path).chain(({ layerId, featureId }) =>
        fromNullable(
            layers.getArray().find(layer => layer.get('id') === layerId),
        )
            .chain(layer => fromNullable(layer.getSource()))
            .chain(source => fromNullable(source.getFeatureById(featureId))),
    );

const filterSelectable: FilterFunction = (_feature, layer) =>
    layer.get('selectable');

const overloadFilter = (filter: FilterFunction | undefined): FilterFunction =>
    fromNullable(filter)
        .map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            filter => (f: FeatureLike, l: Layer<Source, LayerRenderer<any>>) =>
                filterSelectable(f, l) && filter(f, l),
        )
        .getOrElse(filterSelectable);

const initSingle = (
    options: SingleSelectOptions,
    selectedFeature: Collection<Feature<Geometry>>,
    layers: Collection<VectorLayer>,
) => {
    const selectInteraction = new interaction.Select({
        style: options.getStyle ?? selectionStyle(layers),
        features: selectedFeature,
        condition: condition.click,
        multi: false,
        hitTolerance: 3,
        filter: overloadFilter(options.filter),
    });

    const clearSelection = () => {
        selectedFeature.clear();
        selectInteraction.getFeatures().clear();
    };

    const selecteHandler = (event: SelectEvent) => {
        logger('selectInteraction.on select', event.selected);

        if (selectedFeature.getLength() > 0) {
            const f = selectedFeature.item(0);
            const lid = f.get('lid') as string;
            fromNullable(f.getId()).map(fid => options.selectFeature(lid, fid));
            clearSelection();
            selectedFeature.push(f);
        } else {
            options.clearSelection();
        }
    };
    selectInteraction.on('select', selecteHandler);

    layers.addEventListener(AboutToRefreshEvent, () => {
        logger('layers:refresh');
        clearSelection();
        setTimeout(syncSelection, 1);
    });

    const syncSelection = () => {
        clearSelection();
        findFeature(layers, options.getSelected()).map(feature =>
            selectedFeature.push(feature),
        );
    };

    return { selectInteraction, syncSelection };
};

const initMulti = (
    options: MultiSelectOptions,
    selectedFeature: Collection<Feature<Geometry>>,
    layers: Collection<VectorLayer>,
) => {
    const selectInteraction = new interaction.Select({
        style: options.getStyle ?? selectionStyle(layers),
        condition: condition.click,
        // toggleCondition: condition.always,
        multi: true,
        hitTolerance: 3,
        filter: overloadFilter(options.filter),
    });

    const clearSelection = () => {
        selectedFeature.clear();
    };

    const optSelect = options.selectFeatures;

    const selectedPaths = () =>
        catOptions(selectedFeature.getArray().map(pathFromClonedFeature));

    layers.addEventListener(AboutToRefreshEvent, () => {
        logger('layers:refresh');
        clearSelection();
        setTimeout(syncSelection, 1);
    });

    const selecteHandler = (event: SelectEvent) => {
        logger('just:select', event);

        const selected = catOptions(
            selectInteraction
                .getFeatures()
                .getArray()
                .map(pathFromOriginalFeature),
        );
        optSelect(selected);
    };

    selectInteraction.on('select', selecteHandler);

    const selectFeature = (fp: NonNullFeaturePath) =>
        findFeature(layers, fp).map(feature => {
            const cloned = feature.clone();
            cloned.setId(serializeFeaturePath(fp));
            selectedFeature.push(cloned);
            selectInteraction.getFeatures().push(feature);
        });

    const syncSelection = () => {
        const stateSelection = options.getSelected().slice();
        const mapSelection = selectedPaths();
        const interacted = selectInteraction.getFeatures().getArray();
        logger(
            'sync',
            'selection:',
            stateSelection.length,
            'map:',
            mapSelection.length,
            'interaction',
            interacted.length,
        );
        if (!eqFeaturePathList(stateSelection, mapSelection)) {
            clearSelection();
            stateSelection.forEach(selectFeature);
        }
    };

    return { selectInteraction, syncSelection };
};

export const select = (
    options: SelectOptions,
    layers: Collection<VectorLayer>,
    toolsLayers: Collection<VectorLayer>,
) => {
    const selectedFeature = new Collection<Feature<Geometry>>();

    selectedFeature.on('add', event =>
        logger('selectedFeature#add', event.element.getId()),
    );
    selectedFeature.on('remove', event =>
        logger('selectedFeature#remove', event.element.getId()),
    );

    const selectedLayer = new Vector({
        source: new SourceVector({ features: selectedFeature }),
        style: options.getStyle ?? selectionStyle(layers),
    });

    selectedLayer.set('id', '__select__');

    selectedLayer.set('selectable', false);
    toolsLayers.push(selectedLayer);

    const { selectInteraction, syncSelection } = (() => {
        switch (options.tag) {
            case 'single':
                return initSingle(options, selectedFeature, layers);
            case 'multi':
                return initMulti(options, selectedFeature, layers);
        }
    })();

    const init = (map: Map) => {
        map.addInteraction(selectInteraction);
    };

    const update = withInteraction<InteractionSelect>(
        'select',
        () => {
            syncSelection();
            selectInteraction.setActive(true);
        },
        () => {
            syncSelection();
            selectInteraction.setActive(false);
        },
    );

    return { init, update };
};

logger('loaded');
